import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Divider,
  Chip,
  Grid,
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Print as PrintIcon,
  Person as PersonIcon,
  Business as IceIcon,
  LocationOn as LocationIcon,
  CalendarToday as DateIcon,
} from '@mui/icons-material';
import { FactureProforma, FactureProformaStatus } from '../../types/factureProforma';
// generateFactureProformaPDF is dynamically imported in the print handler so
// the @react-pdf/renderer chunk only loads when the user clicks Print.

interface FactureProformaPreviewProps {
  proforma: FactureProforma;
  open: boolean;
  onClose: () => void;
  onEdit: () => void;
  onPrint?: () => void;
}

const FactureProformaPreview: React.FC<FactureProformaPreviewProps> = ({
  proforma,
  open,
  onClose,
  onEdit,
  onPrint,
}) => {
  const getStatusChip = (status: string) => {
    switch (status) {
      case FactureProformaStatus.BROUILLON:
        return <Chip label="Brouillon" color="default" size="small" />;
      case FactureProformaStatus.VALIDEE:
        return <Chip label="Validée" color="primary" size="small" />;
      case FactureProformaStatus.ANNULEE:
        return <Chip label="Annulée" color="error" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { minHeight: '70vh' } }}>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h6" sx={{ color: '#4CAF50', fontWeight: 600 }}>
              Facture Proforma {proforma.reference}
            </Typography>
            {getStatusChip(proforma.status)}
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="subtitle1" gutterBottom sx={{ color: '#4CAF50', fontWeight: 600 }}>
          Informations Générales
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Box display="flex" alignItems="center" gap={1}>
              <DateIcon sx={{ fontSize: 18, color: '#4CAF50' }} />
              <Typography variant="body2">
                <strong>Date:</strong> {new Date(proforma.date).toLocaleDateString('fr-FR')}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Box
          sx={{
            mt: 2,
            p: 2,
            backgroundColor: 'rgba(76, 175, 80, 0.08)',
            borderRadius: 1,
            border: '1px solid rgba(76, 175, 80, 0.3)',
          }}
        >
          <Typography variant="subtitle2" sx={{ color: '#4CAF50', mb: 1.5, fontWeight: 600 }}>
            Informations du Client
          </Typography>
          <Box display="flex" flexDirection="column" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <PersonIcon sx={{ fontSize: 18, color: '#4CAF50' }} />
              <Typography variant="body2">
                <strong>{proforma.client_name || 'N/A'}</strong>
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <IceIcon sx={{ fontSize: 18, color: '#4CAF50' }} />
              <Typography variant="body2" color="text.secondary">
                ICE: {proforma.client_ice || 'N/A'}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <LocationIcon sx={{ fontSize: 18, color: '#4CAF50' }} />
              <Typography variant="body2" color="text.secondary">
                {proforma.client_address || 'N/A'}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle1" gutterBottom sx={{ color: '#4CAF50', fontWeight: 600 }}>
          Articles
        </Typography>
        <TableContainer sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'background.default' }}>
                <TableCell sx={{ width: '40%' }}>Désignation</TableCell>
                <TableCell align="right" sx={{ width: '15%' }}>Quantité</TableCell>
                <TableCell align="right" sx={{ width: '20%' }}>Prix Unitaire</TableCell>
                <TableCell align="right" sx={{ width: '25%' }}>Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {proforma.items.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>{item.designation}</TableCell>
                  <TableCell align="right">{Number(item.quantite).toLocaleString('fr-FR')}</TableCell>
                  <TableCell align="right">
                    {Number(item.prix_unitaire).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                  </TableCell>
                  <TableCell align="right">
                    {Number(item.total).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {proforma.remarque && (
          <>
            <Typography variant="subtitle1" gutterBottom sx={{ color: '#4CAF50', fontWeight: 600 }}>
              Remarque
            </Typography>
            <Box sx={{ p: 2, backgroundColor: 'background.default', borderRadius: 1, mb: 3 }}>
              <Typography variant="body2">{proforma.remarque}</Typography>
            </Box>
          </>
        )}

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom sx={{ color: '#4CAF50', fontWeight: 600 }}>
              Conditions
            </Typography>
            <Box sx={{ backgroundColor: 'background.default', p: 2, borderRadius: 1 }}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Condition de paiement:</Typography>
                <Typography variant="body2">{proforma.condition_paiement_nom || '-'}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Mode de paiement:</Typography>
                <Typography variant="body2">{proforma.mode_paiement_nom || '-'}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2">Délai de réalisation:</Typography>
                <Typography variant="body2">
                  {proforma.delai_realisation ? `${proforma.delai_realisation} jours` : '-'}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom sx={{ color: '#4CAF50', fontWeight: 600 }}>
              Totaux
            </Typography>
            <Box sx={{ backgroundColor: 'background.default', p: 2, borderRadius: 1 }}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Total HT:</Typography>
                <Typography variant="body2">
                  {Number(proforma.total_ht).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">TVA ({Number(proforma.tva_rate)}%):</Typography>
                <Typography variant="body2">
                  {Number(proforma.tva_amount).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mt={2} pt={2} borderTop="2px solid #4CAF50">
                <Typography variant="subtitle1" fontWeight={600}>Total TTC:</Typography>
                <Typography variant="subtitle1" fontWeight={600} color="#4CAF50">
                  {Number(proforma.total_ttc).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {proforma.user_name && (
          <Box sx={{ mt: 3, textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary">
              Créé par: {proforma.user_name}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Fermer
        </Button>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={onPrint}
          sx={{ borderColor: '#4CAF50', color: '#4CAF50' }}
        >
          Imprimer
        </Button>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={onEdit}
          sx={{ backgroundColor: '#4CAF50', '&:hover': { backgroundColor: '#388E3C' } }}
        >
          Modifier
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FactureProformaPreview;
